import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  History,
  LogOut,
  ChevronRight,
  Calendar,
  Star,
  Sparkles,
  Package,
  QrCode,
  Pencil,
  FileText,
  Phone,
  Mail,
  Shield,
} from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppStore } from "../store/useAppStore";
import { sessionsService } from "../services/sessionsService";
import { maxPlatform } from "../services/maxPlatform";
import { visitorAuthService } from "../services/visitorAuthService";
import { eventsService } from "../services/eventsService";
import { catalogService } from "../services/catalogService";
import { visitorSessionService, mapAuthUserToProfile } from "../services/visitorSessionService";
import { visitorAuthApi } from "../api/auth";
import { isApiEnabled } from "../api/config";
import { PageShell } from "../components/PageShell";
import { PageHeader } from "../components/PageHeader";
import { GlassCard } from "../components/GlassCard";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { ShareQrCarousel, type ShareQrCardItem } from "../components/ShareQrCarousel";
import { printableQr } from "../utils/publicQr";
import type { Product, Representative } from "../types";
import { LegalDocumentsSheet } from "../components/LegalDocumentsSheet";

export function ProfilePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    user,
    reset,
    updateUser,
    scanHistory,
    savedContacts,
    selectedEventId,
    eventRole,
    exhibitorCompanyName,
    exhibitorCompanyId,
    exhibitorBadgeQrCode,
    exhibitorBoothQrCode,
    exhibitorBooth,
    exhibitorRepresentativeId,
  } = useAppStore();

  const isExhibitor = eventRole === "exhibitor";
  const sharePhoneOnScan = user.sharePhoneOnScan !== false;
  const shareEmailOnScan = user.shareEmailOnScan !== false;

  const menuItems = [
    ...(isExhibitor
      ? [
          {
            id: "support",
            label: t("profile.menu_support_exhibitor"),
            description: t("profile.menu_support_exhibitor_desc"),
            icon: Sparkles,
            color: "bg-violet-100 text-violet-600",
            path: "/support",
          },
        ]
      : []),
    {
      id: "favorites",
      label: t("profile.menu_favorites"),
      description: t("profile.menu_favorites_desc"),
      icon: Star,
      color: "bg-amber-100 text-amber-500",
      path: "/favorites",
    },
    {
      id: "history",
      label: t("profile.menu_history"),
      description: isExhibitor
        ? t("profile.menu_history_exhibitor_desc")
        : t("profile.menu_history_desc"),
      icon: History,
      color: "bg-purple-100 text-purple-500",
      path: "/history",
    },
    {
      id: "events",
      label: t("profile.menu_events"),
      description: t("profile.menu_events_desc"),
      icon: Calendar,
      color: "bg-emerald-100 text-emerald-500",
      path: "/events",
    },
    {
      id: "documents",
      label: t("profile.menu_documents"),
      description: t("profile.menu_documents_desc"),
      icon: FileText,
      color: "bg-slate-100 text-slate-600",
      path: "__documents__",
    },
  ];

  const initials = user.fullName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2);

  const [sessionFavoritesCount, setSessionFavoritesCount] = useState(0);
  const [companyProducts, setCompanyProducts] = useState<Product[]>([]);
  const [colleagues, setColleagues] = useState<Representative[]>([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [publicPhoneDraft, setPublicPhoneDraft] = useState("");
  const [publicPhoneSaving, setPublicPhoneSaving] = useState(false);
  const [scanPrivacySaving, setScanPrivacySaving] = useState(false);

  const handleScanPrivacyToggle = useCallback(
    async (patch: { sharePhoneOnScan?: boolean; shareEmailOnScan?: boolean }) => {
      if (!isApiEnabled()) return;
      const prev = {
        sharePhoneOnScan: user.sharePhoneOnScan !== false,
        shareEmailOnScan: user.shareEmailOnScan !== false,
      };
      updateUser({
        sharePhoneOnScan: patch.sharePhoneOnScan ?? prev.sharePhoneOnScan,
        shareEmailOnScan: patch.shareEmailOnScan ?? prev.shareEmailOnScan,
      });
      setScanPrivacySaving(true);
      try {
        const res = await visitorAuthApi.updateScanPrivacy(patch);
        updateUser(mapAuthUserToProfile(res.user));
        toast.success(t("profile.scan_privacy_saved"));
      } catch {
        updateUser(prev);
        toast.error(t("profile.scan_privacy_error"));
      } finally {
        setScanPrivacySaving(false);
      }
    },
    [t, updateUser, user.shareEmailOnScan, user.sharePhoneOnScan],
  );

  const myRepresentative = useMemo(() => {
    if (!exhibitorRepresentativeId) return null;
    return colleagues.find((r) => r.id === exhibitorRepresentativeId) ?? null;
  }, [colleagues, exhibitorRepresentativeId]);

  useEffect(() => {
    if (!myRepresentative) {
      setPublicPhoneDraft("");
      return;
    }
    setPublicPhoneDraft(
      (myRepresentative.publicPhone ?? myRepresentative.phone ?? "").trim(),
    );
  }, [myRepresentative]);

  useEffect(() => {
    if (!selectedEventId) {
      setSessionFavoritesCount(0);
      return;
    }
    let cancelled = false;
    sessionsService
      .getUserFavorites(user.id, selectedEventId)
      .then((favorites) => {
        if (!cancelled) setSessionFavoritesCount(favorites.length);
      })
      .catch(() => {
        if (!cancelled) setSessionFavoritesCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [savedContacts, user.id, selectedEventId]);

  useEffect(() => {
    if (!isExhibitor || !selectedEventId || !exhibitorCompanyId) {
      setCompanyProducts([]);
      setColleagues([]);
      return;
    }
    let cancelled = false;
    setShareLoading(true);
    Promise.all([
      catalogService.listProducts(selectedEventId, exhibitorCompanyId, "visitor"),
      catalogService.listRepresentatives(selectedEventId, exhibitorCompanyId, "visitor"),
    ])
      .then(([products, reps]) => {
        if (cancelled) return;
        setCompanyProducts(products);
        setColleagues(reps);
      })
      .catch(() => {
        if (!cancelled) {
          setCompanyProducts([]);
          setColleagues([]);
        }
      })
      .finally(() => {
        if (!cancelled) setShareLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isExhibitor, selectedEventId, exhibitorCompanyId]);

  const shareCards = useMemo((): ShareQrCardItem[] => {
    if (!isExhibitor) return [];
    const cards: ShareQrCardItem[] = [];
    const badgeOpaque = exhibitorBadgeQrCode?.trim();
    if (badgeOpaque) {
      cards.push({
        id: "self",
        kind: "self",
        title: user.fullName || t("profile.menu_card"),
        subtitle: user.position || t("profile.role_exhibitor"),
        qrValue: printableQr(badgeOpaque),
      });
    }
    const boothOpaque = exhibitorBoothQrCode?.trim();
    if (boothOpaque) {
      cards.push({
        id: "booth",
        kind: "booth",
        title: exhibitorCompanyName || t("profile.booth_section"),
        subtitle: exhibitorBooth
          ? `${t("visitor.booth")} ${exhibitorBooth}`
          : t("profile.share_booth_qr"),
        qrValue: printableQr(boothOpaque),
      });
    }
    for (const rep of colleagues) {
      if (exhibitorRepresentativeId && rep.id === exhibitorRepresentativeId) continue;
      if (!rep.badgeQrCode) continue;
      cards.push({
        id: `rep-${rep.id}`,
        kind: "colleague",
        title: rep.fullName,
        subtitle: rep.position || t("profile.colleague"),
        qrValue: rep.badgeQrCode.startsWith("http")
          ? rep.badgeQrCode
          : printableQr(rep.badgeQrCode),
      });
    }
    for (const product of companyProducts) {
      if (!product.qrCode) continue;
      cards.push({
        id: `prod-${product.id}`,
        kind: "product",
        title: product.name,
        subtitle: product.category || t("profile.company_products"),
        qrValue: product.qrCode,
      });
    }
    return cards;
  }, [
    isExhibitor,
    exhibitorBadgeQrCode,
    exhibitorBoothQrCode,
    exhibitorBooth,
    exhibitorCompanyName,
    exhibitorRepresentativeId,
    user.fullName,
    user.position,
    colleagues,
    companyProducts,
    t,
  ]);

  const favoritesCount =
    savedContacts.filter((c) => c.eventId === selectedEventId).length +
    sessionFavoritesCount;

  const roleLabel =
    eventRole === "exhibitor"
      ? t("profile.role_exhibitor")
      : eventRole === "organizer"
        ? t("profile.role_organizer")
        : eventRole === "moderator"
          ? t("profile.role_moderator")
          : t("profile.role_visitor");

  const handleLogout = async () => {
    // In MAX, native confirm is unreliable — logout on the tap itself.
    const confirmed = maxPlatform.isAvailable()
      ? true
      : await maxPlatform.showConfirm(t("profile.logout_confirm"));
    if (!confirmed) return;
    maxPlatform.hapticFeedback("heavy");
    if (isApiEnabled()) {
      visitorAuthService.logout();
      eventsService.clearCache();
    }
    reset();
    navigate(isApiEnabled() ? "/login" : "/", { replace: true });
  };

  const openCard = useCallback(() => {
    maxPlatform.hapticFeedback("light");
    navigate("/card");
  }, [navigate]);

  const handleSavePublicPhone = useCallback(async () => {
    if (!isApiEnabled() || !selectedEventId || !isExhibitor) return;
    const trimmed = publicPhoneDraft.trim();
    if (trimmed) {
      const digits = trimmed.replace(/\D/g, "");
      if (digits.length < 10) {
        toast.error(t("profile.public_phone_error"));
        return;
      }
    }
    setPublicPhoneSaving(true);
    try {
      const patched = await visitorSessionService.updateExhibitorProfile(selectedEventId, {
        publicPhone: trimmed,
      });
      if (patched.representative) {
        setColleagues((prev) => {
          const idx = prev.findIndex((r) => r.id === patched.representative!.id);
          if (idx < 0) return [...prev, patched.representative!];
          const next = [...prev];
          next[idx] = { ...next[idx], ...patched.representative! };
          return next;
        });
        setPublicPhoneDraft(
          (patched.representative.publicPhone ?? patched.representative.phone ?? "").trim(),
        );
      }
      maxPlatform.hapticFeedback("success");
      toast.success(t("profile.public_phone_saved"));
    } catch {
      toast.error(t("visitor.loading_error"));
    } finally {
      setPublicPhoneSaving(false);
    }
  }, [isExhibitor, publicPhoneDraft, selectedEventId, t]);

  const handleClearPublicPhone = useCallback(async () => {
    if (!isApiEnabled() || !selectedEventId || !isExhibitor) return;
    setPublicPhoneSaving(true);
    try {
      const patched = await visitorSessionService.updateExhibitorProfile(selectedEventId, {
        publicPhone: "",
      });
      if (patched.representative) {
        setColleagues((prev) => {
          const idx = prev.findIndex((r) => r.id === patched.representative!.id);
          if (idx < 0) return prev;
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            ...patched.representative!,
            publicPhone: undefined,
          };
          return next;
        });
        setPublicPhoneDraft((patched.representative.phone ?? "").trim());
      } else {
        setPublicPhoneDraft((myRepresentative?.phone ?? "").trim());
      }
      maxPlatform.hapticFeedback("success");
      toast.success(t("profile.public_phone_saved"));
    } catch {
      toast.error(t("visitor.loading_error"));
    } finally {
      setPublicPhoneSaving(false);
    }
  }, [isExhibitor, myRepresentative?.phone, selectedEventId, t]);

  return (
    <>
      <PageHeader title={t("profile.title")} back backTo="/home" right={<LanguageSwitcher />} />
      <PageShell>
        <div className="mt-5 space-y-5">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <GlassCard
              variant="elevated"
              padding="lg"
              className={isExhibitor ? "cursor-pointer active:scale-[0.99] transition-transform" : undefined}
              onClick={isExhibitor ? openCard : undefined}
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-[18px] flex-shrink-0">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[18px] text-gray-900">{user.fullName}</p>
                    {selectedEventId && (
                      <span
                        className={`text-[11px] px-2 py-0.5 rounded-full ${
                          isExhibitor
                            ? "bg-violet-100 text-violet-700"
                            : "bg-sky-100 text-sky-700"
                        }`}
                      >
                        {roleLabel}
                      </span>
                    )}
                  </div>
                  {(exhibitorCompanyName || (user.position && user.companyName)) && (
                    <p className="text-[13px] text-gray-500 truncate">
                      {isExhibitor && exhibitorCompanyName
                        ? exhibitorCompanyName
                        : user.position && user.companyName
                          ? `${user.position} @ ${user.companyName}`
                          : user.companyName}
                    </p>
                  )}
                  {user.phone && <p className="text-[12px] text-gray-500">{user.phone}</p>}
                  {user.email && !user.phone && (
                    <p className="text-[12px] text-gray-500">{user.email}</p>
                  )}
                  {user.tgUsername && (
                    <p className="text-[12px] text-indigo-400">@{user.tgUsername}</p>
                  )}
                </div>
                {isExhibitor ? (
                  <span
                    className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex-shrink-0"
                    title={t("visitor.edit")}
                    aria-label={t("visitor.edit")}
                  >
                    <Pencil size={18} />
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                <div className="flex-1 text-center">
                  <p className="text-[18px] text-gray-900">
                    {selectedEventId
                      ? scanHistory.filter((s) => s.eventId === selectedEventId).length
                      : scanHistory.length}
                  </p>
                  <p className="text-[11px] text-gray-400">{t("profile.stat_scans")}</p>
                </div>
                <div className="w-px h-8 bg-gray-100" />
                <div className="flex-1 text-center">
                  <p className="text-[18px] text-gray-900">{favoritesCount}</p>
                  <p className="text-[11px] text-gray-400">{t("profile.stat_favorites")}</p>
                </div>
                <div className="w-px h-8 bg-gray-100" />
                <div className="flex-1 text-center">
                  <p className="text-[18px] text-gray-900">
                    {selectedEventId
                      ? new Set(
                          scanHistory
                            .filter((s) => s.eventId === selectedEventId && s.companyId)
                            .map((s) => s.companyId),
                        ).size
                      : new Set(scanHistory.map((s) => s.companyId)).size}
                  </p>
                  <p className="text-[11px] text-gray-400">{t("profile.stat_companies")}</p>
                </div>
              </div>
            </GlassCard>
          </motion.div>

          {isApiEnabled() && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.03, duration: 0.35 }}
            >
              <GlassCard variant="default" padding="sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-600 flex items-center justify-center flex-shrink-0">
                    <Shield size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[14px] text-gray-900">{t("profile.scan_privacy_section")}</p>
                    <p className="text-[12px] text-gray-500">{t("profile.scan_privacy_section_desc")}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="flex items-center justify-between gap-3 min-h-11 px-1">
                    <span className="flex items-center gap-2 text-[14px] text-gray-800">
                      <Phone size={16} className="text-gray-400" />
                      {t("profile.scan_privacy_phone")}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={sharePhoneOnScan}
                      disabled={scanPrivacySaving}
                      onClick={() =>
                        void handleScanPrivacyToggle({ sharePhoneOnScan: !sharePhoneOnScan })
                      }
                      className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${
                        sharePhoneOnScan ? "bg-indigo-600" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                          sharePhoneOnScan ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </label>
                  <label className="flex items-center justify-between gap-3 min-h-11 px-1">
                    <span className="flex items-center gap-2 text-[14px] text-gray-800">
                      <Mail size={16} className="text-gray-400" />
                      {t("profile.scan_privacy_email")}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={shareEmailOnScan}
                      disabled={scanPrivacySaving}
                      onClick={() =>
                        void handleScanPrivacyToggle({ shareEmailOnScan: !shareEmailOnScan })
                      }
                      className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${
                        shareEmailOnScan ? "bg-indigo-600" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                          shareEmailOnScan ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </label>
                </div>
              </GlassCard>
            </motion.div>
          )}

          {isExhibitor && isApiEnabled() && selectedEventId && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.04, duration: 0.35 }}
            >
              <GlassCard variant="default" padding="sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                    <Phone size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[14px] text-gray-900">{t("profile.public_phone_section")}</p>
                    <p className="text-[12px] text-gray-500">{t("profile.public_phone_section_desc")}</p>
                  </div>
                </div>
                <label className="block">
                  <span className="sr-only">{t("profile.public_phone_section")}</span>
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={publicPhoneDraft}
                    onChange={(e) => setPublicPhoneDraft(e.target.value)}
                    placeholder={t("profile.public_phone_placeholder")}
                    className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-[14px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                </label>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    disabled={publicPhoneSaving}
                    onClick={() => void handleSavePublicPhone()}
                    className="flex-1 h-10 rounded-xl bg-indigo-600 text-white text-[13px] active:bg-indigo-700 disabled:opacity-50"
                  >
                    {t("profile.public_phone_save")}
                  </button>
                  {myRepresentative?.publicPhone ? (
                    <button
                      type="button"
                      disabled={publicPhoneSaving}
                      onClick={() => void handleClearPublicPhone()}
                      className="h-10 px-3 rounded-xl bg-gray-100 text-gray-600 text-[13px] active:bg-gray-200 disabled:opacity-50"
                    >
                      {t("profile.public_phone_clear")}
                    </button>
                  ) : null}
                </div>
              </GlassCard>
            </motion.div>
          )}

          {isExhibitor && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.35 }}
            >
              <GlassCard variant="default" padding="sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center flex-shrink-0">
                    <QrCode size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[14px] text-gray-900">{t("profile.share_qr_section")}</p>
                    <p className="text-[12px] text-gray-500">{t("profile.share_qr_section_desc")}</p>
                  </div>
                </div>
                {shareLoading ? (
                  <p className="text-center text-[12px] text-gray-400 py-5">{t("common.loading")}</p>
                ) : shareCards.length === 0 ? (
                  <div className="flex items-center gap-3 py-2 px-1">
                    <Package size={18} className="text-gray-300" />
                    <p className="text-[13px] text-gray-400">{t("profile.share_qr_empty")}</p>
                  </div>
                ) : (
                  <ShareQrCarousel items={shareCards} />
                )}
              </GlassCard>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.35 }}
            className="space-y-2"
          >
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <GlassCard
                  key={item.id}
                  variant="default"
                  padding="sm"
                  className="cursor-pointer active:scale-[0.98] transition-transform"
                  onClick={() => {
                    maxPlatform.hapticFeedback("light");
                    if (item.path === "__documents__") {
                      setDocumentsOpen(true);
                      return;
                    }
                    navigate(item.path);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0`}
                    >
                      <Icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] text-gray-900">{item.label}</p>
                      <p className="text-[12px] text-gray-500">{item.description}</p>
                    </div>
                    <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                  </div>
                </GlassCard>
              );
            })}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.35 }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void handleLogout();
              }}
              className="w-full flex items-center justify-center gap-2 h-11 rounded-xl bg-red-50 text-red-500 text-[14px] active:bg-red-100 transition-colors select-none [-webkit-user-select:none] touch-manipulation"
            >
              <LogOut size={16} aria-hidden />
              <span>{t("profile.logout")}</span>
            </button>
          </motion.div>

          <p className="text-center text-[11px] text-gray-300 pb-4">ConferenceApp v0.1.0</p>
        </div>
      </PageShell>
      <LegalDocumentsSheet open={documentsOpen} onClose={() => setDocumentsOpen(false)} />
    </>
  );
}
